import apiClient from "../common";
import { mapCoverageListFromApi } from "../coverage/mapCoverageListResponse";
import type { ProductCoverage } from "../types";
import type { SimulationBootstrapPayload } from "./createSimulation";
import { mapHttpErrorToSimulationLoadError } from "./loadSavedSimulation";
import type { SimulatedPurchaseOrderRef } from "./simulationsList";
import {
  buildPeriodDeviationKey,
  buildPeriodKey,
  type SimulationDeviationNotesState,
} from "views/admin/simulator/types/simulationDeviationNotes";

export type SimulationSessionPeriodDeviation = {
  erp_code: string;
  period_key: string;
  period_start_date?: string | null;
  period_index?: number | null;
  deviation_note_id: string;
  deviation_note_name?: string;
  simulation_result_product_id?: string | null;
  comments?: string;
};

export type SimulationSessionPayload = {
  simulation: {
    id: string;
    simulation_name: string;
    status: string;
    created_at: string | null;
    executed_at: string | null;
    closed_at: string | null;
  };
  simulated_purchase_orders: SimulatedPurchaseOrderRef[];
  chronology: string;
  coverage: Array<Record<string, unknown>> | null;
  coverage_source:
    | "snapshot"
    | "missing"
    | "chronology_mismatch"
    | "invalid_snapshot";
  snapshot_chronology: string | null;
  deviation_notes: {
    by_product_period: SimulationSessionPeriodDeviation[];
    by_product_general: Record<
      string,
      {
        deviation_note_id: string;
        deviation_note_name?: string;
        comments?: string;
      }
    >;
  };
};

export type LoadedSimulationSession = {
  bootstrap: SimulationBootstrapPayload;
  coverage: ProductCoverage[] | null;
  chronology: string;
  coverageFromSnapshot: boolean;
  deviationHydration: Partial<SimulationDeviationNotesState> | null;
  /** Payload crudo del GET session (para re-hidratar desviaciones tras cargar cobertura). */
  rawSession: SimulationSessionPayload;
};

function resolvePeriodIndexForNote(
  productRow: ProductCoverage | undefined,
  periodKey: string,
  chronology: string,
  fallbackIndex: number,
): number {
  if (!productRow || !periodKey || periodKey === "unknown") {
    return fallbackIndex;
  }
  const matchedIdx = productRow.coverage.findIndex(
    (period) => buildPeriodKey(period.targetDate, chronology) === periodKey,
  );
  return matchedIdx >= 0 ? matchedIdx : fallbackIndex;
}

export function buildDeviationHydration(
  session: SimulationSessionPayload,
  coverage: ProductCoverage[] | null,
  chronology: string = session.chronology,
): Partial<SimulationDeviationNotesState> | null {
  const erpToProductId = new Map<string, number>();
  const productRowByErp = new Map<string, ProductCoverage>();
  if (coverage) {
    for (const row of coverage) {
      const code = String(row.productProps.erpCode ?? "").trim();
      if (code) {
        erpToProductId.set(code, row.productProps.id);
        productRowByErp.set(code, row);
      }
    }
  }

  const byProductPeriod: SimulationDeviationNotesState["byProductPeriod"] = {};
  for (const note of session.deviation_notes.by_product_period ?? []) {
    const erpCode = String(note.erp_code ?? "").trim();
    const productId = erpToProductId.get(erpCode);
    if (!productId || !note.deviation_note_id) {
      continue;
    }
    const periodKey = String(note.period_key ?? "").trim() || "unknown";
    const storedIndex = Number(note.period_index ?? 0);
    const periodIndex = resolvePeriodIndexForNote(
      productRowByErp.get(erpCode),
      periodKey,
      chronology,
      storedIndex,
    );
    const compositeKey = buildPeriodDeviationKey(productId, periodIndex);
    byProductPeriod[compositeKey] = {
      productId,
      erpCode,
      periodKey,
      periodStartDate: note.period_start_date ?? undefined,
      periodIndex,
      deviationNoteId: String(note.deviation_note_id),
      deviationNoteLabel: note.deviation_note_name ?? undefined,
      comments: note.comments,
      simulationResultProductId:
        note.simulation_result_product_id != null
          ? String(note.simulation_result_product_id)
          : undefined,
    };
  }

  const byProductGeneral: SimulationDeviationNotesState["byProductGeneral"] =
    {};
  for (const [erpCode, general] of Object.entries(
    session.deviation_notes.by_product_general ?? {},
  )) {
    const code = String(erpCode).trim();
    const productId = erpToProductId.get(code);
    const noteId = general?.deviation_note_id;
    if (productId && noteId) {
      byProductGeneral[productId] = {
        deviationNoteId: String(noteId),
        label: general.deviation_note_name ?? String(noteId),
      };
    }
  }

  const partial: Partial<SimulationDeviationNotesState> = {};
  if (Object.keys(byProductPeriod).length > 0) {
    partial.byProductPeriod = byProductPeriod;
  }
  if (Object.keys(byProductGeneral).length > 0) {
    partial.byProductGeneral = byProductGeneral;
  }
  return Object.keys(partial).length > 0 ? partial : null;
}

/** Desvíos persistidos de una simulación (fuente: GET session, filtrado por simulation_id). */
export async function fetchSimulationDeviationHydration(
  simulationId: string,
  chronology: string,
  coverage: ProductCoverage[] | null,
): Promise<Partial<SimulationDeviationNotesState>> {
  let session = await apiLoadSimulationSession(simulationId, chronology);
  if (
    session.coverage_source === "chronology_mismatch" &&
    session.snapshot_chronology
  ) {
    session = await apiLoadSimulationSession(
      simulationId,
      session.snapshot_chronology,
    );
  }
  return (
    buildDeviationHydration(session, coverage, session.chronology) ?? {
      byProductGeneral: {},
      byProductPeriod: {},
    }
  );
}

export async function apiLoadSimulationSession(
  simulationId: string,
  chronology: string = "year_month",
): Promise<SimulationSessionPayload> {
  const res = await apiClient.get(`simulations/${simulationId}/session/`, {
    params: { chronology },
  });
  return res.data as SimulationSessionPayload;
}

/** Carga sesión persistida en un solo request: metadatos, cobertura snapshot y desviaciones. */
export async function loadSavedSimulationSession(
  simulationId: string,
  chronology: string = "year_month",
): Promise<LoadedSimulationSession> {
  try {
    let session = await apiLoadSimulationSession(simulationId, chronology);
    if (
      session.coverage_source === "chronology_mismatch" &&
      session.snapshot_chronology
    ) {
      session = await apiLoadSimulationSession(
        simulationId,
        session.snapshot_chronology,
      );
    }
    const effectiveChronology = session.chronology;
    const coverageRows = session.coverage;
    const coverage =
      Array.isArray(coverageRows) && coverageRows.length > 0
        ? mapCoverageListFromApi(coverageRows)
        : null;

    const bootstrap: SimulationBootstrapPayload = {
      simulationId: session.simulation.id,
      simulationName: session.simulation.simulation_name || undefined,
      simulatedPurchaseOrders: session.simulated_purchase_orders ?? [],
    };

    return {
      bootstrap,
      coverage,
      chronology: effectiveChronology,
      coverageFromSnapshot: session.coverage_source === "snapshot",
      deviationHydration: buildDeviationHydration(
        session,
        coverage,
        effectiveChronology,
      ),
      rawSession: session,
    };
  } catch (err) {
    throw mapHttpErrorToSimulationLoadError(err);
  }
}
